// v0.5.0g — KL-1 closed. The "DATA MISTAKE → drop this transaction
// from this filing" button is now ENABLED. Clicking it supersedes
// the calc with `exclude_txn_ids = [txn.id]` and the operator's
// audit reason. The backend write_registry_entries hook then
// stamps an entry in `vat_excluded_txn_registry` with status
// `open`. While `open`, the calc's XLSX export is blocked by the
// pre-export gate (routes_export.py) — the operator MUST resolve
// the entry at /admin/excluded-backlog before filing.
//
// This was the original "silent under-reporting" gap KL-1
// documented. The button stays honest: it shows the operator
// the consequence (export blocked until resolved at the
// backlog page) BEFORE they confirm.
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { errorMessage } from "../../lib/errorMessage";
import { useGuardedSupersede } from "../../workflow/useGuardedSupersede";
import { DROP_REASONS } from "../../lib/reviewReasonPresets";

export const InspectKL1Panel = ({
  offenderRate, calcId, txnIds, txnCount, country, onDropped,
}) => {
  const { t } = useTranslation();
  const [reason, setReason] = useState("");
  const [preset, setPreset] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  // v0.6.10 — live phase from the supersede job. A multi-minute
  // destructive operation that renders as a single frozen word is what
  // made an operator run it twice; see supersedeCalcAsync's comment.
  const [phase, setPhase] = useState(null);
  const [showInfo, setShowInfo] = useState(false);
  const navigate = useNavigate();
  // A1-93 — dropping rows supersedes: same plan as Step 6 (form's rates).
  const { plan, supersedeAsync } = useGuardedSupersede(calcId);

  const valid = (reason || "").trim().length >= 20
    && !/[<>]/.test(reason);

  const doDrop = async () => {
    if (!calcId || !txnIds || txnIds.length === 0) return;
    if (busy) return;   // belt-and-braces; the button is disabled too
    setBusy(true); setErr(null); setPhase("starting");
    try {
      const data = await supersedeAsync({
        reason: reason.trim(),
        exclude_txn_ids: txnIds,
      }, (job) => setPhase(job?.progress?.phase || job?.status || null));
      if (onDropped) onDropped(data);
      const newId = data?.new_calc_id;
      if (newId) {
        navigate(`/admin/excluded-backlog`);
      }
    } catch (e) {
      setErr(errorMessage(e));
    } finally {
      setBusy(false);
      setPhase(null);
    }
  };

  return (
    <div
      data-testid={`inspect-kl1-panel-${offenderRate}`}
      className="text-left p-4 rounded-lg border-2 border-amber-500 bg-amber-50 dark:bg-amber-950/30"
    >
      <div className="font-bold text-sm text-amber-900 dark:text-amber-100">
        {t("ui.country_return.kl1.title", { defaultValue: "Data error — drop and resolve later" })}
      </div>
      <div className="text-xs leading-snug text-amber-900 dark:text-amber-100 mt-2">
        {txnCount === 1
          ? t("ui.country_return.kl1.body_one", {
            country: country || t("ui.country_return.kl1.filing_fallback", { defaultValue: "filing" }),
            defaultValue: `Drop this transaction from the ${country || "filing"}. The dropped transaction go into the dropped-transactions backlog, and the spreadsheet export stays blocked until you record their resolution there as either “filed in a later calculation” or “not a real sale”.`,
          })
          : t("ui.country_return.kl1.body_other", {
            count: txnCount, country: country || t("ui.country_return.kl1.filing_fallback", { defaultValue: "filing" }),
            defaultValue: `Drop these ${txnCount} transactions from the ${country || "filing"}. The dropped transactions go into the dropped-transactions backlog, and the spreadsheet export stays blocked until you record their resolution there as either “filed in a later calculation” or “not a real sale”.`,
          })}
      </div>
      {/* v0.6.10 — pick, don't write. Same treatment as the review
          queue's acknowledge paths, for uniformity; see
          lib/reviewReasonPresets for why "Other" survives. */}
      <select
        data-testid={`inspect-kl1-preset-${offenderRate}`}
        value={preset}
        onChange={(e) => {
          setPreset(e.target.value);
          setReason(e.target.value);
        }}
        className="w-full mt-3 bg-white dark:bg-slate-900 border border-amber-400 dark:border-amber-700 text-slate-900 dark:text-slate-100 rounded px-2 py-1.5 text-xs"
      >
        <option value="">{t("ui.country_return.kl1.reason_pick", { defaultValue: "Why is this a data error?" })}</option>
        {DROP_REASONS.map((r) => <option key={r} value={r}>{r}</option>)}
      </select>
      {!valid && (
        <div className="mt-1 text-[10px] text-amber-900 dark:text-amber-200">
          {!reason.trim()
            ? t("ui.country_return.kl1.choose_reason", { defaultValue: "Choose a reason above to enable the drop." })
            : t("ui.country_return.kl1.more_chars", {
              count: Math.max(0, 20 - reason.trim().length),
              defaultValue: `${Math.max(0, 20 - reason.trim().length)} more character(s) needed.`,
            })}
        </div>
      )}
      {busy && (
        <div
          data-testid={`inspect-kl1-progress-${offenderRate}`}
          className="mt-2 text-[11px] text-amber-900 dark:text-amber-100"
        >
          {t("ui.country_return.kl1.busy_note", {
            defaultValue: "This re-runs the whole calculation and takes a few minutes. It keeps running if you navigate away — don’t start it again.",
          })}
        </div>
      )}
      {plan.blocked && (
        <div data-testid={`inspect-kl1-blocked-${offenderRate}`} className="mt-2 text-xs text-amber-900 dark:text-amber-100">
          {plan.blocked}
        </div>
      )}
      {err && (
        <div
          data-testid={`inspect-kl1-error-${offenderRate}`}
          className="mt-2 text-[11px] text-rose-700 dark:text-rose-300"
        >
          {err}
        </div>
      )}
      <div className="mt-2 flex flex-wrap gap-2 items-center">
        <button
          type="button"
          data-testid={`inspect-kl1-confirm-${offenderRate}`}
          data-resolution-class="self-resolve"
          data-resolution-endpoint="POST /api/runs/calc/{old_id}/supersede/async"
          disabled={busy || !valid || !!plan.blocked}
          title={plan.blocked || undefined}
          onClick={doDrop}
          className="text-xs font-bold px-3 py-1.5 rounded-md bg-rose-600 hover:bg-rose-700 disabled:opacity-50 disabled:cursor-not-allowed text-white"
        >
          {busy
            ? `${t("ui.country_return.kl1.dropping", { defaultValue: "Dropping…" })} ${phase ? `(${phase})` : ""}`.trim()
            : txnCount === 1
              ? t("ui.country_return.kl1.drop_one", { defaultValue: "▶ Drop 1 transaction from this filing" })
              : t("ui.country_return.kl1.drop_other", { count: txnCount, defaultValue: `▶ Drop ${txnCount} transactions from this filing` })}
        </button>
        <button
          type="button"
          data-testid={`inspect-kl1-toggle-${offenderRate}`}
          onClick={() => setShowInfo(!showInfo)}
          className="text-[11px] underline text-slate-600 dark:text-slate-300"
        >
          {showInfo ? t("ui.country_return.kl1.hide", { defaultValue: "Hide" }) : t("ui.country_return.kl1.what_happens", { defaultValue: "ⓘ What happens after I drop?" })}
        </button>
      </div>
      {showInfo && (
        <div
          data-testid={`inspect-kl1-explainer-${offenderRate}`}
          className="mt-3 p-3 rounded-md border border-amber-400 dark:border-amber-700 bg-white/60 dark:bg-slate-900/40 text-[11px] leading-relaxed text-amber-900 dark:text-amber-100"
        >
          <div className="font-bold mb-1">{t("ui.country_return.kl1.explainer_title", { defaultValue: "What dropping does:" })}</div>
          <ol className="list-decimal list-inside space-y-1 mb-2">
            <li>{txnCount === 1
              ? t("ui.country_return.kl1.step1_one", { defaultValue: "Replaces this calculation with a new one that excludes the dropped transaction." })
              : t("ui.country_return.kl1.step1_other", { defaultValue: "Replaces this calculation with a new one that excludes the dropped transactions." })}</li>
            <li>{txnCount === 1
              ? t("ui.country_return.kl1.step2_one", { defaultValue: "Adds an entry to the dropped-transactions backlog (status: unresolved)." })
              : t("ui.country_return.kl1.step2_other", { count: txnCount, defaultValue: `Adds ${txnCount} entries to the dropped-transactions backlog (status: unresolved).` })}</li>
            <li>{t("ui.country_return.kl1.step3", { defaultValue: "Blocks the new calculation's spreadsheet export until every entry is resolved." })}</li>
            <li>{t("ui.country_return.kl1.step4", { defaultValue: "Takes you to the backlog page to resolve." })}</li>
          </ol>
          <div className="opacity-80">
            {t("ui.country_return.kl1.options_lead", { defaultValue: "Resolution options on the backlog:" })}{" "}
            <span className="font-semibold">{t("ui.country_return.kl1.options_filed_later", { defaultValue: "filed in a later calculation" })}</span>{" "}
            {t("ui.country_return.kl1.options_filed_later_note", { defaultValue: "(you name the later calculation) or" })}{" "}
            <span className="font-semibold">{t("ui.country_return.kl1.options_not_a_sale", { defaultValue: "not a real sale (bad data)" })}</span>{" "}
            {t("ui.country_return.kl1.options_not_a_sale_note", { defaultValue: "(you record why)." })}
          </div>
        </div>
      )}
    </div>
  );
};

export default InspectKL1Panel;
